import fuzzy from 'fuzzy';
import _ from 'lodash';

import {KeyboardActionsEnum} from '../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {defineMessage} from '../../../../libs/shared/helpers/src/internationalization/message';
import {PredicateOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {makeEnumTypeGuard} from '../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {LocalizeRenderer} from '../../../../srcCommon/internationalization/internationalizationContext';
import {VisualCustomView} from '../../../helpers/models/customViewHelpers';
import {isTeammateNamespace} from '../../../helpers/models/namespaceHelpers';
import {InboxModel} from '../../../models/api/inboxModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {TeamModel} from '../../../models/api/teamModel';
import {TeammateOnlineStatusesEnum} from '../../../store/data/teammates/onlineTeammatesSelectors';
import {SidePanelFrame} from '../../../store/stateless/sidePanelFrameSelectors';
import {Filter} from '../cells/header/cellsHeaderFilters';

export enum PaletteItemTypesEnum {
  ROUTE = 'ROUTE',
  INBOX = 'INBOX',
  TEAMMATE = 'TEAMMATE',
  TEAM = 'TEAM',
  CUSTOM_VIEW = 'CUSTOM_VIEW',
  FRAME = 'FRAME',
  FILTER = 'FILTER',
  ACTION = 'ACTION',
}

export enum PaletteCategoriesEnum {
  INTEGRATIONS = 'integrations',
  ASSIGNED_CONVERSATIONS = 'assignedConversations',
  MY_INBOX = 'myInbox',
  TAGS = 'tags',
  FILTERS = 'filters',
  UNREAD = 'unread',
  TEAMS = 'teams',
  FRONT_APPS = 'frontapp',
}
export const isPaletteCategory = makeEnumTypeGuard(PaletteCategoriesEnum);

export const paletteCategoriesRenderer: {[id in PaletteCategoriesEnum]: LocalizeRenderer} = {
  [PaletteCategoriesEnum.UNREAD]: (localize) =>
    localize(
      defineMessage({
        id: 'palette-category-unread',
        message: 'Unread',
      }),
    ),
  [PaletteCategoriesEnum.MY_INBOX]: (localize) =>
    localize(
      defineMessage({
        id: 'palette-category-myinbox',
        message: 'Me',
      }),
    ),
  [PaletteCategoriesEnum.TAGS]: (localize) =>
    localize(
      defineMessage({
        id: 'palette-category-tags',
        message: 'Tags',
      }),
    ),
  [PaletteCategoriesEnum.FILTERS]: (localize) =>
    localize(
      defineMessage({
        id: 'palette-category-filters',
        message: 'Filters',
      }),
    ),
  [PaletteCategoriesEnum.TEAMS]: (localize) =>
    localize(
      defineMessage({
        id: 'palette-category-teams',
        message: 'Team inboxes',
      }),
    ),
  [PaletteCategoriesEnum.ASSIGNED_CONVERSATIONS]: (localize) =>
    localize(
      defineMessage({
        id: 'palette-category-assignedconversations',
        message: 'Assigned conversations',
      }),
    ),
  [PaletteCategoriesEnum.INTEGRATIONS]: (localize) =>
    localize(
      defineMessage({
        id: 'palette-category-integrations',
        message: 'Integrations',
      }),
    ),
  [PaletteCategoriesEnum.FRONT_APPS]: (localize) =>
    localize(
      defineMessage({
        id: 'palette-category-frontapps',
        message: 'Front apps',
      }),
    ),
};
export const paletteCategoriesOrders = {
  [PaletteCategoriesEnum.UNREAD]: 100,
  [PaletteCategoriesEnum.MY_INBOX]: 90,
  [PaletteCategoriesEnum.TAGS]: 80,
  [PaletteCategoriesEnum.FILTERS]: 70,
  [PaletteCategoriesEnum.TEAMS]: 60,
  [PaletteCategoriesEnum.ASSIGNED_CONVERSATIONS]: 50,
  [PaletteCategoriesEnum.INTEGRATIONS]: 40,
  [PaletteCategoriesEnum.FRONT_APPS]: 30,
};

export interface PaletteItemBase<T extends PaletteItemTypesEnum> {
  type: T;
  itemId: string | number;
  label: string;
  isBold?: boolean;
  keyboardAction?: KeyboardActionsEnum;
  parentCategoryId?: PaletteCategoriesEnum | string;
  score?: number;
}

export interface PaletteFilterItem extends PaletteItemBase<PaletteItemTypesEnum.FILTER> {
  filter: Filter;
  icon: IconName;
}

export interface PaletteTeammateItem extends PaletteItemBase<PaletteItemTypesEnum.TEAMMATE> {
  teammate: TeammateModel;
  path: string;
  onlineStatus: TeammateOnlineStatusesEnum;
  isPinned: boolean;
}

export interface PaletteRouteItem extends PaletteItemBase<PaletteItemTypesEnum.ROUTE> {
  path: string;
  icon: IconName;
  counterValue?: number;
  counterKey?: string;
}

export interface PaletteInboxItem extends PaletteItemBase<PaletteItemTypesEnum.INBOX> {
  inbox: InboxModel;
  counterValue?: number;
  path: string;
  isPinned: boolean;
}

export interface PaletteCustomViewItem extends PaletteItemBase<PaletteItemTypesEnum.CUSTOM_VIEW> {
  customView: VisualCustomView;
  path: string;
}

export interface PaletteTeamItem extends PaletteItemBase<PaletteItemTypesEnum.TEAM> {
  team: TeamModel;
  path: string;
}

export interface PaletteFrameItem extends PaletteItemBase<PaletteItemTypesEnum.FRAME> {
  frame: SidePanelFrame;
}

export interface PaletteActionItem extends PaletteItemBase<PaletteItemTypesEnum.ACTION> {
  icon: IconName;
}

export type PaletteItem =
  | PaletteCustomViewItem
  | PaletteFilterItem
  | PaletteFrameItem
  | PaletteActionItem
  | PaletteInboxItem
  | PaletteRouteItem
  | PaletteTeamItem
  | PaletteTeammateItem;

interface FuzzyPaletteSearchItem {
  score: number;
  item: PaletteItem;
}

export function fuzzySearchPalette(
  list: ReadonlyArray<PaletteItem>,
  query: string,
  getItemImplicitPrefix: (item: PaletteItem) => string | undefined,
  showScores: boolean,
): ReadonlyArray<PaletteItem> {
  return _(list)
    .filter((item) => 'label' in item)
    .map((item) => {
      // 1. Construct a string to match on
      const [prefix, label] = [
        getItemImplicitPrefix(item) ? `${getItemImplicitPrefix(item)}: ` : '',
        item.label,
      ];
      const string = `${prefix}${label}`;

      // 2. Match the string with prefix
      const matchOnBaseString = fuzzy.match(query, string, {caseSensitive: false});
      // 3. Match the label alone
      const matchOnLabel = fuzzy.match(query, label || prefix, {caseSensitive: false});
      // 4. Get scores for both matches
      const baseScore = matchOnBaseString?.score;
      const labelScore = matchOnLabel?.score;
      // 5. Compute an average of our scores.
      const score = [baseScore, labelScore].reduce((a, b) => a + b, 0) / 2;

      return {
        item: {
          ...item,
          score: showScores ? score : undefined,
        },
        score,
      };
    })
    .filter(({score}) => score > 0)
    .map((item) => maybeInflateScore(item))
    .orderBy([(i) => i.score, (i) => i.item.parentCategoryId || ''], ['desc', 'asc'])
    .map((r) => r.item)
    .value();
}

function maybeInflateScore(item: FuzzyPaletteSearchItem) {
  const conditions: ReadonlyArray<PredicateOf<FuzzyPaletteSearchItem>> = [
    // 1. Private inboxes.
    (i) => i.item.type === PaletteItemTypesEnum.INBOX && isTeammateNamespace(i.item.inbox.namespace),
    // 2. Pinned inboxes.
    (i) => i.item.type === PaletteItemTypesEnum.INBOX && i.item.isPinned,
    // 3. Pinned teammate.
    (i) => i.item.type === PaletteItemTypesEnum.TEAMMATE && i.item.isPinned,
    // 4. Unpinned inboxes.
    (i) => i.item.type === PaletteItemTypesEnum.INBOX && !i.item.isPinned,
    // 5. Unpinned teammates.
    (i) => i.item.type === PaletteItemTypesEnum.TEAMMATE && !i.item.isPinned,
  ];

  const newItem = {...item};

  conditions.forEach((predicate, index, array) => {
    if (predicate(item)) {
      newItem.score += array.length - (index + 1);
    }
  });

  return newItem;
}
